import React, {useCallback, useRef, useState} from 'react';
import styled from 'styled-components';
import {Typography as Text} from '../typography/Typography';
import root from 'window-or-global';
import {SubnavItemNumber} from './SubnavItemNumber';
import {AccentColorOptionsPartial, useBreakpointDown} from 'epic-ue-theme';
import {AnimateOnViewWrap} from '../../wraps';
import classNames from 'classnames';
import {useSwipeable} from 'react-swipeable';

export interface SubnavItemProps {
    title: string;
    anchorId: string;
    isHoverOther?: boolean;
    index: number;
    active?: boolean;
    accentColor?: AccentColorOptionsPartial;
    onMouseEnter?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

const NavItem = styled.a<{accentColor: AccentColorOptionsPartial}>`
    padding-left: ${(p) => `${p.theme.spacing(2)}px`};
    padding-right: ${(p) => `${p.theme.spacing(2)}px`};
    text-decoration: none;
    color: ${(p) => p.theme.palette.text.primary};
    font-family: ${(p) => p.theme.fonts.brutal.light};
    transition: color 0.2s ${(p) => p.theme.easing};
    display: inline-block;
    padding-top: 10px;
    padding-bottom: 10px;
    margin: 7px 0;

    span {
        font-family: ${(p) => p.theme.fonts.brutal.regular};
    }

    /* MW-1741 */
    .loc-de .subnav-item-title {
        @media (min-width: 1280px) and (max-width: 1500px) {
            letter-spacing: 1px;
        }
    }

    ${(p) => p.theme.breakpoints.up('md')} {
        padding-left: ${(p) => `${p.theme.spacing(2)}px`};
        padding-right: ${(p) => `${p.theme.spacing(2)}px`};
    }
    ${(p) => p.theme.breakpoints.up('xxl')} {
        padding-left: ${(p) => `${p.theme.spacing(4)}px`};
        padding-right: ${(p) => `${p.theme.spacing(4)}px`};
    }
    @media (min-width: 1280px) and (max-width: 1400px) {
        padding-left: ${(p) => `${p.theme.spacing(1)}px`};
        padding-right: ${(p) => `${p.theme.spacing(1)}px`};
    }

    &.isHoverOther {
        color: ${(p) =>
            p.theme.ifLight(p, 'theme.palette.neutral.steel', 'theme.palette.neutral.silver')};
    }

    &.active {
        color: ${(p) =>
            p.accentColor ? p.theme.palette.accent[p.accentColor] : p.theme.palette.accent.sky};
    }

    &:hover {
        ${(p) => p.theme.breakpoints.up('md')} {
            color: ${(p) => p.theme.palette.text.primary};
        }
    }

    &:focus-visible {
        outline: none;
        & > * {
            outline: 1px solid ${(p) => p.theme.palette.misc.outline};
        }
    }
`;

export const SubnavItem = ({
    title,
    anchorId,
    index,
    active,
    accentColor,
    onMouseEnter,
    onMouseLeave,
    isHoverOther
}: SubnavItemProps): JSX.Element => {
    const ref = useRef<HTMLAnchorElement>(null);
    const [swiping, setSwiping] = useState(false);
    const onLinkClick = useCallback(
        (e) => {
            if (swiping) return;
            const target = e.currentTarget || {};
            const hrefAttribute = target.getAttribute('href') || '';
            if (hrefAttribute) {
                const el = root.document.getElementById(hrefAttribute.replace('#', ''));
                if (el) {
                    e.preventDefault();
                    //one pixel down keeps the nav just barely in sticky mode with the back to top and download
                    root.scrollTo({left: 0, top: el.offsetTop + 1, behavior: 'smooth'});
                }
            }
        },
        [swiping]
    );

    const handlers = useSwipeable({
        onSwiping: () => {
            if (!swiping) {
                setSwiping(true);
            }
        },
        onSwiped: () => {
            setSwiping(false);
        }
    });

    const disableAnim = useBreakpointDown('sm');

    return (
        <NavItem
            {...handlers}
            ref={ref}
            key={title}
            id={`nav-item-${index}`}
            className={classNames('subnav-item', {
                active,
                isHoverOther
            })}
            href={`#${anchorId || ''}`}
            onClick={onLinkClick}
            accentColor={accentColor}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}>
            <AnimateOnViewWrap
                key={`subnavitem-${index}`}
                translateY={8}
                duration={0.4}
                delay={0.2 * index + 0.1}
                disable={disableAnim}
                triggerOnce>
                <Text variant="h4" as="span">
                    <SubnavItemNumber index={index} />
                    <span className="subnav-item-title">{title}</span>
                </Text>
            </AnimateOnViewWrap>
        </NavItem>
    );
};
